/**
 * Media URL resolver for different cloud providers
 * 100% browser-safe - no server-side logic
 */

import { R, assertRuntime } from '@/config/runtime';
import { supabase } from '@/integrations/supabase/client';
import { POSTER_FALLBACK_PATH } from '@/utils/fallbacks';
import { buildCloudinaryVideoUrls, safeHead } from './cloudinary';
import { minfo, mwarn } from '@/utils/mediaDebug';

// Check if video debugging is enabled
const VIDEO_DEBUG = import.meta.env.VITE_VIDEO_DEBUG === '1';

// In-memory cache to prevent duplicate resolutions
const urlCache = new Map<string, string>();

export type MediaHint = string; // e.g. "cloudinary:public_id" or "supabase:bucket/key"

// Cloudinary ID validation pattern: alphanumeric, slashes, underscores, hyphens
const CLD_ID_RE = /^[a-zA-Z0-9/_-]+$/;

/**
 * Normalize and validate cloud hints before processing
 * Returns normalized hint or null if invalid
 */
export function normalizeCloudHint(hint: string): string | null {
  if (!hint) return null;
  
  // Already a URL (Supabase, direct URLs, etc.)
  if (hint.startsWith('http')) return hint;
  
  // Cloudinary hint validation
  if (hint.startsWith('cloudinary:')) {
    const id = hint.split(':')[1]?.trim();
    if (id && CLD_ID_RE.test(id)) {
      return `cloudinary:${id}`;
    }
    // Invalid Cloudinary ID format
    if (VIDEO_DEBUG) {
      console.warn('⚠️ RESOLVER: Invalid Cloudinary ID format:', id);
    }
    return null;
  }
  
  // Supabase hint
  if (hint.startsWith('supabase:')) {
    return hint;
  }
  
  return hint;
}

// Helper: Cloudinary base URL
const CLOUD_BASE = (cloud: string) =>
  `https://res.cloudinary.com/${cloud}/video/upload`;

// Helper: Generate lean, universally playable MP4 URL
const toMp4 = (publicId: string, cloud = R.CLOUD_NAME) =>
  `${CLOUD_BASE(cloud)}/f_mp4,vc_auto,q_auto:eco,w_640/${publicId}.mp4`;

// Helper: Generate poster image URL from video
const toPoster = (publicId: string, cloud = R.CLOUD_NAME) =>
  `https://res.cloudinary.com/${cloud}/video/upload/f_jpg,q_auto:good,c_limit,w_400/${publicId}.jpg`;

// Helper: Check if hint is Cloudinary format
const isCloudinaryHint = (hint: string) => hint.startsWith('cloudinary:');

/**
 * Resolves a playable MP4 URL from various cloud providers
 * @param hint - Format: "cloudinary:public_id" or direct URL
 */
export async function resolvePlayableMp4(hint: string): Promise<string> {
  assertRuntime(); // throws early in dev if misconfigured

  if (!hint) {
    throw new Error('❌ RESOLVER: Missing hint parameter');
  }

  // Normalize and validate hint
  const normalizedHint = normalizeCloudHint(hint);
  if (!normalizedHint) {
    throw new Error(`❌ RESOLVER: Invalid or malformed media hint: "${hint}"`);
  }

  // Check cache first (use original hint as key)
  if (urlCache.has(hint)) {
    if (VIDEO_DEBUG) console.log('✅ RESOLVER: Cache hit for', hint);
    return urlCache.get(hint)!;
  }

  if (VIDEO_DEBUG) console.log('🔍 RESOLVER: Resolving', normalizedHint);

  let resolvedUrl: string;

  // Handle Cloudinary hints: "cloudinary:my_folder/my_public_id"
  if (isCloudinaryHint(normalizedHint)) {
    const publicId = normalizedHint.replace('cloudinary:', '').replace(/\.mp4$/i, '');
    resolvedUrl = toMp4(publicId);
    if (VIDEO_DEBUG) console.log('🎬 RESOLVER: Cloudinary URL:', resolvedUrl);
  }
  // Handle Supabase hints (requires signed URL via storage API)
  else if (normalizedHint.startsWith('supabase:')) {
    const path = normalizedHint.replace('supabase:', '');
    resolvedUrl = await resolveSupabaseVideo(path);
    if (VIDEO_DEBUG) console.log('☁️ RESOLVER: Supabase URL:', resolvedUrl);
  }
  // Fallthrough: if it's already a URL, use it
  else if (/^https?:\/\//i.test(normalizedHint)) {
    resolvedUrl = normalizedHint;
    if (VIDEO_DEBUG) console.log('🔗 RESOLVER: Direct URL:', resolvedUrl);
  }
  else {
    throw new Error(`Unsupported media hint: "${normalizedHint}"`);
  }

  // Cache the resolved URL
  urlCache.set(hint, resolvedUrl);
  
  return resolvedUrl;
}

/**
 * Resolve poster image URL from cloud hint
 * @param cloudHint - Same format as video hint
 */
export async function resolvePosterImage(cloudHint: string): Promise<string> {
  if (!cloudHint) {
    return POSTER_FALLBACK_PATH;
  }

  // Normalize and validate hint
  const normalizedHint = normalizeCloudHint(cloudHint);
  if (!normalizedHint) {
    if (VIDEO_DEBUG) {
      console.warn('⚠️ RESOLVER: Invalid poster hint, using fallback:', cloudHint);
    }
    return POSTER_FALLBACK_PATH;
  }

  if (isCloudinaryHint(normalizedHint)) {
    const publicId = normalizedHint.replace('cloudinary:', '').replace(/\.mp4$/i, '');
    return toPoster(publicId);
  }

  if (normalizedHint.startsWith('supabase:')) {
    const path = normalizedHint.replace('supabase:', '');
    const posterPath = path.replace(/\.[^/.]+$/, '.jpg');
    return resolveSupabaseImage(posterPath);
  }

  return POSTER_FALLBACK_PATH;
}

/**
 * Resolve Supabase Storage video URL (requires async for signed URLs)
 */
async function resolveSupabaseVideo(path: string): Promise<string> {
  if (!path) {
    throw new Error('❌ RESOLVER: Missing Supabase storage path');
  }

  try {
    const [bucket, ...fileParts] = path.split('/');
    const filePath = fileParts.join('/');
    
    if (!bucket || !filePath) {
      throw new Error(`❌ RESOLVER: Invalid Supabase path format: ${path}`);
    }

    // Try signed URL first (for private buckets)
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      if (VIDEO_DEBUG) {
        console.warn('⚠️ RESOLVER: Signed URL failed, trying public URL:', error.message);
      }
      // Fallback to public URL
      const { data: publicData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      
      return publicData.publicUrl;
    }

    return data.signedUrl;
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ RESOLVER: Supabase video resolution failed:', msg);
    throw new Error(`Failed to resolve Supabase video: ${msg}`);
  }
}

/**
 * Resolve Supabase image URL (public or signed)
 */
async function resolveSupabaseImage(path: string): Promise<string> {
  try {
    const [bucket, ...fileParts] = path.split('/');
    const filePath = fileParts.join('/');
    
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  } catch {
    return POSTER_FALLBACK_PATH;
  }
}

// ============================================================
// LEGACY COMPATIBILITY FUNCTIONS (for existing components)
// ============================================================

export type PlayableItem = {
  is_video: boolean;
  video_provider?: 'cloudinary' | 'supabase' | null;
  delivery_type?: 'upload' | 'authenticated' | 'private' | null;
  cloud_public_id?: string | null;
  video_360_path?: string | null;
  original_url?: string | null;
};

export function resolvePlayableSrc(options: {
  publicId: string;
  mode: string;
  ua?: { isiOS: boolean };
  autoPlay?: boolean;
  userHasInteracted?: boolean;
}) {
  const { publicId, mode, autoPlay = false } = options;
  
  const srcUrl = mode === 'lite' 
    ? toMp4(publicId)
    : `${CLOUD_BASE(R.CLOUD_NAME)}/f_mp4,q_auto:good/${publicId}`;
    
  return {
    srcUrl,
    srcType: 'video/mp4',
    posterUrl: toPoster(publicId),
    preload: mode === 'lite' ? 'none' : 'metadata',
    shouldAutoPlay: autoPlay
  };
}

export async function resolvePlayableMp4Legacy(item: PlayableItem): Promise<string> {
  if (!item?.is_video) return '';
  
  if (item.video_provider === 'cloudinary' && item.cloud_public_id) {
    return toMp4(item.cloud_public_id);
  }
  
  if (item.video_provider === 'supabase' && item.video_360_path) {
    return resolveSupabaseVideo(item.video_360_path);
  }
  
  return item.original_url || '';
}

// ============================================================
// ============================================================
// MEDIA STACK v2: Unified resolver
// ============================================================

export type ResolveInput = {
  provider: 'cloudinary' | 'none';
  cloudPublicId?: string;
  mode: 'lite' | 'full' | 'public';
};

export type ResolveOutput = {
  playableUrl?: string;
  posterUrl: string;
  kind: 'video' | 'image' | 'none';
};

export type MediaResolution =
  | { kind: "video"; mp4Url: string; posterUrl: string }
  | { kind: "image"; posterUrl: string };

const CLD_IMAGE_BASE = (cloud: string) => `https://res.cloudinary.com/${cloud}/image/upload`;
const CLD_VIDEO_BASE = (cloud: string) => `https://res.cloudinary.com/${cloud}/video/upload`;

export function buildPosterUrl(publicId?: string, w = 960, h = 540): string {
  if (!publicId) return POSTER_FALLBACK_PATH;
  
  // Don't add .jpg if the ID already looks malformed or empty
  if (publicId.length < 3) return POSTER_FALLBACK_PATH;
  
  // For video IDs, extract a frame at 0s using video delivery path
  // Note: so_0 extracts first frame, works for all videos
  return `${CLD_VIDEO_BASE(R.CLOUD_NAME)}/f_jpg,q_auto,w_${w},h_${h},c_fill,so_0/${publicId}.jpg`;
}

/**
 * Async resource detection for Cloudinary assets
 * Handles 404s gracefully by detecting whether the asset is video or image
 */
export async function resolveMediaV2Async(pid: string): Promise<MediaResolution> {
  const base = `https://res.cloudinary.com/${R.CLOUD_NAME}`;
  const posterUrl = `${base}/video/upload/so_0,q_70/${pid}.jpg`;
  const mp4Url = `${base}/video/upload/f_mp4,q_auto:eco,ac_none,vc_h264/${pid}.mp4`;

  // Try video poster first
  const posterOk = await safeHead(posterUrl, 2500);
  if (!posterOk) {
    // Try image domain for IDs that were uploaded as images
    const imagePoster = `${base}/image/upload/q_70/${pid}.jpg`;
    if (await safeHead(imagePoster, 2500)) {
      return { kind: "image", posterUrl: imagePoster };
    }
    // Final fallback
    return { kind: "image", posterUrl: POSTER_FALLBACK_PATH };
  }

  // Poster exists, now check if MP4 is available
  if (await safeHead(mp4Url, 2500)) {
    return { kind: "video", mp4Url, posterUrl };
  }

  // MP4 missing but poster exists → treat as image-only
  return { kind: "image", posterUrl };
}

export async function resolvePlayableMp4V2(input: ResolveInput): Promise<string> {
  if (input.provider !== 'cloudinary' || !input.cloudPublicId) {
    throw new Error('No playable provider');
  }
  
  // Use hardened Cloudinary URL builder
  const built = buildCloudinaryVideoUrls(input.cloudPublicId);
  
  if (!built) {
    throw new Error(`Invalid Cloudinary public_id: ${input.cloudPublicId}`);
  }

  minfo("resolvePlayableMp4V2", { publicId: built.publicId, mp4Url: built.mp4Url, source: "cloudinary" });

  // Optional: preflight HEAD to avoid trying known-404s
  const ok = await safeHead(built.mp4Url);
  if (!ok) {
    mwarn("mp4 preflight failed; using fallback", { publicId: built.publicId, tried: built.mp4Url });
    throw new Error(`Cloudinary video not found: ${built.publicId}`);
  }
  
  return built.mp4Url;
}

export function resolveMediaV2(input: ResolveInput, opts?: { posterId?: string }): ResolveOutput {
  if (input.provider !== 'cloudinary' || !input.cloudPublicId) {
    return { kind: 'none', posterUrl: POSTER_FALLBACK_PATH };
  }
  
  // Use hardened poster URL from the same builder
  const built = buildCloudinaryVideoUrls(opts?.posterId ?? input.cloudPublicId);
  
  minfo("resolveMediaV2", { publicId: built?.publicId, posterUrl: built?.posterUrl, source: "cloudinary" });
  
  return {
    kind: 'video',
    posterUrl: built?.posterUrl ?? POSTER_FALLBACK_PATH,
  };
}

// DEBUG TOOLS
// ============================================================

/**
 * Debug test harness for console testing
 * Usage: window.__debugResolve('cloudinary:my_video_id')
 */
export async function __debugResolve(cloudHint: string) {
  console.group('🔧 DEBUG RESOLVER');
  console.log('Input:', cloudHint);
  console.log('Cache size:', urlCache.size);
  console.log('Cached keys:', Array.from(urlCache.keys()));
  
  try {
    const result = await resolvePlayableMp4(cloudHint);
    console.log('✅ Result:', result);
    console.groupEnd();
    return result;
  } catch (error) {
    console.error('❌ Error:', error);
    console.groupEnd();
    throw error;
  }
}

// Expose debug function in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).__debugResolve = __debugResolve;
  console.log('🔧 Debug function available: window.__debugResolve(cloudHint)');
}